import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SwUpdate } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { LoggerService } from '@core/services/infra/logger.service';

/**
 * sessionStorage key shared with the inline boot watchdog in `index.html`.
 * Set once a recovery hard-reload is attempted so neither path can loop.
 */
const BOOT_RECOVERY_GUARD = 'proporio:boot-recovered';

/**
 * @domain Core/Services
 *
 * Surfaces service-worker version updates to the user. When the Angular SW has
 * fetched and prepared a new app version (`VERSION_READY`), we show a snackbar
 * with a "reload" action. Reloading swaps in the fresh `index.html` together
 * with its matching hashed chunks — the safety net against the classic
 * "new index.html + stale chunk → white screen / chunk-load error" after a
 * deploy.
 *
 * No-op when the SW is disabled (dev, Karma) so it stays inert in tests.
 */
@Injectable({ providedIn: 'root' })
export class SwUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly logger = inject(LoggerService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Begin listening for version updates. Call once from the app root.
   * Safe to call when the SW is disabled — it simply returns.
   */
  init(): void {
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates
      .pipe(
        filter(event => event.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.logger.info('[SwUpdateService] New app version ready', 'SwUpdateService');
        this.promptReload();
      });

    // When the SW reports it can no longer recover — corrupt CacheStorage, or a
    // hashed chunk that no longer exists on the server after a deploy (Firebase
    // then rewrites the missing chunk to index.html → "text/html" → the app is
    // stuck) — the user is effectively frozen. Purge caches, drop the SW and
    // hard-reload ONCE. Shares the boot-watchdog guard in index.html so the two
    // recovery paths never double-reload within the same session.
    this.swUpdate.unrecoverable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
      this.logger.error(
        '[SwUpdateService] Unrecoverable SW state — purging caches and reloading',
        'SwUpdateService',
        { reason: event.reason }
      );
      void this.purgeAndReload();
    });
  }

  private promptReload(): void {
    const ref = this.snackBar.open(
      this.translate.instant('shared.swUpdate.available'),
      this.translate.instant('shared.swUpdate.reload'),
      { panelClass: ['info-notification', 'custom-notification'] }
    );

    ref.onAction().subscribe(() => {
      // Reload pulls the activated SW's fresh assets in lockstep.
      document.location.reload();
    });
  }

  /**
   * Last-resort recovery from a broken service-worker cache: wipe every
   * CacheStorage entry, unregister the SW, then hard-reload so the next load
   * fetches a fresh, self-consistent index.html + chunks straight from the
   * network. Guarded by a sessionStorage flag (shared with the index.html boot
   * watchdog) so a still-broken reload can't spin into an infinite loop.
   */
  private async purgeAndReload(): Promise<void> {
    try {
      if (sessionStorage.getItem(BOOT_RECOVERY_GUARD)) return;
      sessionStorage.setItem(BOOT_RECOVERY_GUARD, '1');
    } catch {
      // sessionStorage unavailable (private mode / blocked) — proceed anyway.
    }

    try {
      if (typeof caches !== 'undefined') {
        const keys = await caches.keys();
        await Promise.all(keys.map(key => caches.delete(key)));
      }
      if (navigator.serviceWorker) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(registration => registration.unregister()));
      }
    } catch (err) {
      this.logger.warn(
        '[SwUpdateService] Cache/SW purge failed before reload',
        'SwUpdateService',
        { error: String(err) }
      );
    } finally {
      document.location.reload();
    }
  }
}
